// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import updateShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/UpdateShipTo";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    currentShipTo: getCurrentShipToState(state).value,
    currentShipTosDataView: getCurrentShipTosDataView(state),
    currentBillTo: getCurrentBillToState(state).value,
    countries: getCurrentCountries(state),
    shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
    customerSettings: getSettingsCollection(state).customerSettings,
});

const mapDispatchToProps = {
    updateShipTo,
    setCurrentShipTo,
    loadCurrentShipTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentShippingAddressStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    contentText?: TypographyProps;
    editLink?: LinkPresentationProps;
    editLinkWrapper?: InjectableCss;
}

export const currentShippingAddressStyles: CurrentShippingAddressStyles = {
    wrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.ui3")};
            padding-top: 15px;
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h5",
        as: "h5",
    },
    contentText: {
        css: css``,
    },
    editLink: {
        icon: {
            iconProps: {
                src: "Create",
                color: "secondary",
                size: 14,
            },
            position: "left",
        },
        typographyProps: {
            color: "secondary",
        },
        css: css`
            margin-right: 10px;
        `,
    },
    editLinkWrapper: {
        css: css`
            display: flex !important;
            flex-direction: row !important;
            align-items: baseline;
            margin-top: 20px;
        `,
    },
};

const styles = currentShippingAddressStyles;

const CurrentShippingAddress: React.FunctionComponent<Props> = (props: Props) => {
    const { currentShipTo, currentBillTo, currentShipTosDataView, loadCurrentShipTos } = props;

    useEffect(() => {
        // we reset all dataviews when adding a address, so we have to load these if they go away
        if (!currentShipTosDataView.value && !currentShipTosDataView.isLoading) {
            loadCurrentShipTos();
        }
    });

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    if (!currentShipTo || !currentBillTo || !currentShipTosDataView.value) {
        return null;
    }

    const billToAsShipTo = currentShipTosDataView.value.find(o => o.id === currentBillTo.id);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const editClickHandler = () => {
        setModalIsOpen(true);
    };
    const useBillingAddressHandler = () => {
        if (!billToAsShipTo) {
            return;
        }
        props.setCurrentShipTo({
            shipToId: billToAsShipTo.id,
        });
    };
    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };
    const formSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        if (!currentBillTo || !currentShipTo) {
            return;
        }

        props.updateShipTo({
            billToId: currentBillTo.id,
            shipTo: {
                ...currentShipTo,
                ...address,
            },
            expand: ["validation"],
        });
        modalCloseHandler();
    };

    const { countries, shipToAddressFields, customerSettings } = props;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="addressInfoDisplay_CurrentShippingAddress">
            <Typography {...styles.titleText}>{translate("Shipping Address")}</Typography>
            <AddressInfoDisplay
                firstName={currentShipTo.firstName}
                lastName={currentShipTo.lastName}
                companyName={currentShipTo.companyName}
                attention={currentShipTo.attention}
                address1={currentShipTo.address1}
                address2={currentShipTo.address2}
                address3={currentShipTo.address3}
                address4={currentShipTo.address4}
                city={currentShipTo.city}
                state={currentShipTo.state ? currentShipTo.state.abbreviation : undefined}
                postalCode={currentShipTo.postalCode}
                country={currentShipTo.country ? currentShipTo.country.abbreviation : undefined}
                phone={currentShipTo.phone}
                fax={currentShipTo.fax}
                email={currentShipTo.email}
            />

            <StyledWrapper {...styles.editLinkWrapper}>
                {countries && customerSettings.allowShipToAddressEdit && (
                    <Link {...styles.editLink} onClick={editClickHandler} data-test-selector="shipTo_Edit_Address">
                        <Typography>{translate("Edit")}</Typography>
                    </Link>
                )}
                {billToAsShipTo && currentShipTo.id !== billToAsShipTo.id && (
                    <StyledWrapper>
                        <UseBillingAddressButton onClick={useBillingAddressHandler} />
                    </StyledWrapper>
                )}
            </StyledWrapper>

            {countries && shipToAddressFields && (
                <Modal
                    headline={translate("Edit Shipping Information")}
                    isOpen={modalIsOpen}
                    handleClose={modalCloseHandler}
                >
                    <CustomerAddressForm
                        address={currentShipTo}
                        countries={countries}
                        addressFieldDisplayCollection={shipToAddressFields}
                        onCancel={formCancelHandler}
                        onSubmit={formSubmitHandler}
                    />
                </Modal>
            )}
        </StyledWrapper>
    );
};

interface UseBillingAddressButtonProps {
    onClick: () => void;
}

const UseBillingAddressButton: React.FunctionComponent<UseBillingAddressButtonProps> = (
    props: UseBillingAddressButtonProps,
) => {
    return (
        <Link aria-label={translate("Use Billing Address")} {...styles.editLink} onClick={props.onClick}>
            {translate("Use Billing Address")}
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrentShippingAddress),
    definition: {
        group: "Addresses",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
